import { useQueryClient, useMutation } from '@tanstack/vue-query'
import { useConsultationsApi } from './use-consultations'

export function useScheduleConsultation() {
  const queryClient = useQueryClient()
  const { scheduleConsultation } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ patientId, specialty, customPromptId }) => scheduleConsultation({ patientId, specialty, customPromptId }),
    onError: async (error) => {
      console.log('error', error)
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
    },
  })
}

export function useStartRecording() {
  const { startConsultationRecording } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId }) =>
      startConsultationRecording({ consultationId }),
    onError: async (error) => {
      console.log('error', error)
    },
  })
}

export function useRequestConsultationReport() {
  const queryClient = useQueryClient()
  const { requestConsultationReport } = useConsultationsApi()

  return useMutation({
    mutationFn: ({
      consultationId,
      reason,
      notes,
      specialty,
      customPromptId,
    }) =>
      requestConsultationReport({
        consultationId,
        reason,
        notes,
        specialty,
        customPromptId,
      }),
    onSuccess: async (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['consultation'],
      })
    },
    onError: async (error) => {
      console.log('requestConsultationReport mutation error', error)
    },
  })
}

export function useCancelConsultation() {
  const { cancelConsultation } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId, endDate }) => {
      cancelConsultation({
        consultationId,
        endDate,
      })
    },
    onError: async (error) => {
      console.log('error', error)
    },
  })
}

export function useEditConsultation() {
  const queryClient = useQueryClient()
  const { editConsultationReport } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId, reportId, content }) => {
      editConsultationReport({
        consultationId,
        reportId,
        content,
      })
    },
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(
        ['consultation', variables.consultationId],
        (oldData) => {
          const newData = {
            ...oldData,
            reports: oldData.reports.map((report) =>
              report.id === variables.reportId
                ? { ...report, editedContent: JSON.parse(variables.content) }
                : report,
            ),
          }
          return newData
        },
      )
    },
    onError: async (error) => {
      console.log('error', error)
    },
  })
}

export function useSendConsultationAudio() {
  const { sendConsultationAudio } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId, file, duration, recordingId }) =>
      sendConsultationAudio({
        consultationId,
        file,
        duration,
        recordingId,
      }),
  })
}

export function useSendConsultationAudioChunk() {
  const { sendConsultationAudioChunk } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId, recordingId, chunkIndex, file, duration }) =>
      sendConsultationAudioChunk({
        consultationId,
        recordingId,
        chunkIndex,
        file,
        duration,
      }),
  })
}

export function useFinishConsultationRecording() {
  const { finishConsultationRecording } = useConsultationsApi()

  return useMutation({
    mutationFn: ({
      consultationId,
      recordingId,
      reason,
      notes,
      specialty,
      customPromptId,
    }) =>
      finishConsultationRecording({
        consultationId,
        recordingId,
        reason,
        notes,
        specialty,
        customPromptId,
      }),
  })
}

export function useSendConsultationExamFiles() {
  const { sendConsultationExamFiles } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId, files }) =>
      sendConsultationExamFiles({
        consultationId,
        files,
      }),
  })
}

export function useGetConsultationStatus() {
  const { getConsultationStatus } = useConsultationsApi()

  return useMutation({
    mutationFn: ({ consultationId }) =>
      getConsultationStatus({ consultationId }),
  })
}
